import React from 'react';
import { Box, Typography, Grid, Chip, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDateTimeAsSent } from '../../../utils/dateUtils';
import { showAmount } from './ledgerAmount';

/**
 * Per-bill breakdown shown inside the transaction detail view.
 * Renders nothing unless the transaction is a BILL.
 */
const LedgerBillDetails = ({ transaction, billDetails, loading }) => {
    if (!transaction || transaction.ledger_type !== 'BILL') return null;
    
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!billDetails || billDetails.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No bill details available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        p: 1.5,
        bgcolor: 'background.default',
        borderRadius: 1,
        mt: 2
      }}>
        {billDetails.map((detail, index) => (
          <Accordion 
            key={index}
            defaultExpanded={transaction.ledger_type === 'BILL'}
            sx={{ 
              mb: 1,
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              '&.Mui-expanded': {
                margin: '0 0 8px 0'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Bill ID: {detail.BillID}
                </Typography>
                <Chip 
                  label={detail.Exchange} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Basic Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Basic Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Client ID</Typography>
                      <Typography variant="body2">{detail.ClientID}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Created At</Typography>
                      <Typography variant="body2">{formatDateTimeAsSent(detail.CreatedAt)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Trading Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Trading Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Trading Symbol</Typography>
                      <Typography variant="body2">{detail.TradingSymbol}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{detail.Description}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Buy Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Buy Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Quantity</Typography>
                      <Typography variant="body2">{detail.BuyQty}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Rate</Typography>
                      <Typography variant="body2">{showAmount(detail.BuyRate)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">{showAmount(detail.BuyAmount)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Sell Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sell Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Quantity</Typography>
                      <Typography variant="body2">{detail.SellQty}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Rate</Typography>
                      <Typography variant="body2">{showAmount(detail.SellRate)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="body2">{showAmount(detail.SellAmount)}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* P&L */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    P&L
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: detail.PNL >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 500
                    }}
                  >
                    {showAmount(detail.PNL)}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
};

export default LedgerBillDetails;
